import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '../components/Button/Button';
import { RevealableInput } from '../components/RevealableInput/RevealableInput';
import { useFocusTrap } from '../components/useFocusTrap';
import { useChangePassword } from './useAuth';
import './AuthDialog.css';

/**
 * Signed-in password rotation. Opens from the AccountChip menu.
 *
 * Asks for the current password before accepting a new one — a
 * stolen session cookie shouldn't be enough to lock the real owner
 * out of their account. The backend revokes every other session on
 * success, so this is also the kid's "boot anyone else who might be
 * signed in as me" button.
 */
export interface ChangePasswordDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ChangePasswordDialog({
  isOpen,
  onClose,
}: ChangePasswordDialogProps): JSX.Element | null {
  const titleId = useId();
  const currentId = useId();
  const newId = useId();
  const confirmId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [done, setDone] = useState(false);

  const change = useChangePassword();

  useEffect(() => {
    if (!isOpen) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setDone(false);
      change.reset();
      return;
    }
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useFocusTrap(isOpen, dialogRef);

  if (!isOpen) return null;

  const newValid = newPassword.length >= 8;
  const confirmMatches = confirmPassword === newPassword && confirmPassword.length > 0;
  const canSubmit =
    currentPassword.length > 0 && newValid && confirmMatches && !change.isPending;

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!canSubmit) return;
    change.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => setDone(true),
      },
    );
  };

  return (
    <div
      className="bl-auth__backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="bl-auth"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={dialogRef}
      >
        <header className="bl-auth__header">
          <h2 id={titleId} className="bl-auth__title">
            Change your password
          </h2>
          <button type="button" className="bl-auth__close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>

        {done ? (
          <>
            <p className="bl-auth__sent" role="status">
              Done — your new password is set. Other devices you were signed in on have been
              signed out.
            </p>
            <div className="bl-auth__actions">
              <Button type="button" onClick={onClose}>
                Close
              </Button>
            </div>
          </>
        ) : (
          <form className="bl-auth__form" onSubmit={handleSubmit}>
            <div className="bl-auth__field">
              <label htmlFor={currentId} className="bl-auth__label">
                Current password
              </label>
              <RevealableInput
                id={currentId}
                autoComplete="current-password"
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                data-autofocus
              />
            </div>

            <div className="bl-auth__field">
              <label htmlFor={newId} className="bl-auth__label">
                New password
                <span className="bl-auth__hint"> (8 or more characters)</span>
              </label>
              <RevealableInput
                id={newId}
                autoComplete="new-password"
                required
                minLength={8}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>

            <div className="bl-auth__field">
              <label htmlFor={confirmId} className="bl-auth__label">
                Type it again to be sure
              </label>
              <RevealableInput
                id={confirmId}
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                aria-invalid={
                  confirmPassword.length > 0 && !confirmMatches ? true : undefined
                }
                aria-describedby={
                  confirmPassword.length > 0 && !confirmMatches ? 'bl-pw-mismatch' : undefined
                }
              />
              {confirmPassword.length > 0 && !confirmMatches && (
                <p
                  id="bl-pw-mismatch"
                  className="bl-auth__inline-notice bl-auth__inline-notice--warn"
                >
                  Those don&apos;t match yet.
                </p>
              )}
            </div>

            <div className="bl-auth__actions">
              <Button type="submit" disabled={!canSubmit}>
                {change.isPending ? 'Working…' : 'Save new password'}
              </Button>
            </div>

            {change.error && (
              <p role="alert" className="bl-auth__error">
                {change.error.message}
              </p>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
